import { pageIcon } from './MaskIcon';

// Single source of truth for the site's page → sub-page → section hierarchy
// (DJB-106/107). Both the desktop left nav and the mobile pill menu derive
// from this tree, so nav order and labels live in exactly one place.
//
// - `path` is the page route; `search` is the query string that selects a
//   sub-page tab inside that route. The FIRST sub-page of a page is the tab
//   the route opens by default.
// - DJB-264: a sub-page may instead carry its own `path` (a route of its
//   own, e.g. Inbox › Contact Requests at /inbox/contact). `badge` names the
//   key in the inbox counts (InboxCountsContext) whose to-do number is shown
//   next to that sub-page; the page shows the sum of its sub-page badges.
// - `anchor` is the DOM id of the section element inside the sub-page; the
//   left nav navigates to the sub-page and then scrolls the anchor into view.
// - `icon` is a component reference; the nav renders whatever the slot holds.
//   DJB-121: the real per-page SVGs from public/ replaced the react-icons
//   placeholders, tinted via currentColor through a CSS mask (see MaskIcon).
// DJB-123: top-level page order is Dashboard, Inbox, Accounts, Library, Curator,
// Configuration (supersedes DJB-111's Accounts-first ordering). Accounts
// stays the default landing page — that's routing (App.js), not order.
export const NAV_TREE = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: pageIcon('/dashboard.svg'),
    subPages: [],
  },
  {
    // Website contact and song requests (webforms)
    key: 'inbox',
    label: 'Inbox',
    path: '/inbox',
    icon: pageIcon('/inbox.svg'),
    subPages: [
      // DJB-264: each kind is its own route with a to-do badge in the nav
      {
        key: 'contact',
        label: 'Contact Requests',
        path: '/inbox/contact',
        badge: 'contactOpen',
        sections: [],
      },
      { key: 'songs', label: 'Song Requests', path: '/inbox/songs', badge: 'songsOpen', sections: [] },
    ],
  },
  {
    // DJB-110: page renamed from "Machines"; the /machines route is kept so
    // existing links and bookmarks stay valid
    key: 'accounts',
    label: 'Accounts',
    path: '/machines',
    icon: pageIcon('/accounts.svg'),
    subPages: [
      // DJB-129: sub-page renamed from "List view"; DJB-211: renamed again from
      // "Accounts Info" to "Overview" and "Accounts Manager" to "Manager" (the
      // page name already says "Accounts"). key/search stay 'list'/'accounts' so
      // existing deep links keep working
      // DJB-130: the "Detail view" sub-page is gone — the detail containers
      // render inline on Overview when a row is selected
      { key: 'list', label: 'Overview', search: '?tab=list', sections: [] },
      // DJB-239: Maps sits between Overview and Manager; the shell has no
      // sections yet — the map itself lands in DJB-240
      { key: 'maps', label: 'Maps', search: '?tab=maps', sections: [] },
      {
        key: 'accounts',
        label: 'Manager',
        search: '?tab=accounts',
        sections: [
          { label: 'Create Machine', anchor: 'section-create-machine' },
          { label: 'Groups', anchor: 'section-groups' },
        ],
      },
      {
        // DJB-208: fleet-wide settings; hosts the Optional Features (DJB-209)
        // and Actions (DJB-210) sections
        key: 'settings',
        label: 'Settings',
        search: '?tab=settings',
        sections: [
          { label: 'Optional Features', anchor: 'section-optional-features' },
          { label: 'Actions', anchor: 'section-actions' },
        ],
      },
    ],
  },
  {
    key: 'library',
    label: 'Library',
    path: '/musicmanager',
    icon: pageIcon('/library.svg'),
    subPages: [
      {
        key: 'browse',
        label: 'Browse',
        search: '?tab=browse',
        sections: [
          { label: 'Library Browser', anchor: 'section-library-browser' },
        ],
      },
      {
        key: 'upload',
        label: 'Upload',
        search: '?tab=upload',
        // DJB-269: the Song Requests section sits under the upload
        sections: [
          { label: 'Upload', anchor: 'section-upload' },
          { label: 'Song Requests', anchor: 'section-song-requests' },
        ],
      },
      {
        // DJB-268: Tags is its own sub-page again (DJB-100 had parked it as a
        // section under Upload)
        key: 'tags',
        label: 'Tags',
        search: '?tab=tags',
        sections: [],
      },
    ],
  },
  {
    key: 'curator',
    label: 'Curator',
    path: '/playlists',
    icon: pageIcon('/curator.svg'),
    subPages: [
      {
        key: 'curator',
        label: 'Playlist Studio',
        search: '?tab=curator',
        sections: [
          { label: 'Playlist Editor', anchor: 'section-playlist-editor' },
          { label: 'Category Editor', anchor: 'section-category-editor' },
        ],
      },
      {
        key: 'mediastudio',
        label: 'Media Studio',
        search: '?tab=mediastudio',
        sections: [
          // Keeps the pre-existing Designer anchor so old deep links and the
          // nav point at the same element
          { label: 'Ads & Screensaver Editor', anchor: 'media-studio-designer' },
          { label: 'Background Editor', anchor: 'section-background-editor' },
        ],
      },
      {
        key: 'blocklists',
        label: 'Subscriptions & Restrictions',
        search: '?tab=blocklists',
        sections: [
          { label: 'Playlist Subscriptions', anchor: 'section-playlist-subscriptions' },
          { label: 'Ads & Screensaver Subscriptions', anchor: 'section-ad-subscriptions' },
          { label: 'Background subscriptions', anchor: 'section-background-subscriptions' },
          { label: 'Blocked Content', anchor: 'section-blocked-content' },
        ],
      },
    ],
  },
  {
    key: 'configuration',
    label: 'Configuration',
    path: '/configuration',
    icon: pageIcon('/config.svg'),
    subPages: [
      {
        key: 'pricebonus',
        label: 'Price & Bonus Config',
        search: '?tab=pricebonus',
        sections: [
          { label: 'Paymentmodel', anchor: 'section-paymentmodel' },
          { label: 'Bonus Tiers', anchor: 'section-bonus-tiers' },
          { label: 'LongTrack Length', anchor: 'section-longtrack-length' },
        ],
      },
      {
        key: 'ondisk',
        label: 'OnDisk Policy Config',
        search: '?tab=ondisk',
        sections: [],
      },
      {
        key: 'subscriptions',
        label: 'Config Subscriptions',
        search: '?tab=subscriptions',
        // DJB-179: one Playlist-Subscriptions-style section per config type
        sections: [
          { label: 'Payment Mode Subscriptions', anchor: 'section-config-payment-subscriptions' },
          { label: 'Bonus Configuration Subscriptions', anchor: 'section-config-bonus-subscriptions' },
          { label: 'Long-Track Price Subscriptions', anchor: 'section-config-longtrack-subscriptions' },
          { label: 'On-Disk Policy Subscriptions', anchor: 'section-config-disk-subscriptions' },
        ],
      },
    ],
  },
];

// DJB-264: where a sub-page lives — its own route, or a ?tab= inside the page
export const subPageHref = (page, subPage) =>
  subPage.path ?? `${page.path}${subPage.search}`;

// DJB-112: a section deep-link carries the anchor as ?section= so the target
// page can expand that section (they default to collapsed)
export const sectionHref = (page, subPage, section) => {
  const href = subPageHref(page, subPage);
  return `${href}${href.includes('?') ? '&' : '?'}section=${encodeURIComponent(section.anchor)}`;
};

// The sub-page the location is on: a route match for own-route sub-pages,
// else the ?tab= match; without one — or with a stale key the page no longer
// knows — the pages fall back to their first tab, so the nav does the same.
export const findActiveSubPage = (page, location) => {
  if (!page || page.subPages.length === 0) {
    return undefined;
  }
  const byPath = page.subPages.find(
    (subPage) => subPage.path && location.pathname.startsWith(subPage.path)
  );
  if (byPath) {
    return byPath;
  }
  const tab = new URLSearchParams(location.search).get('tab');
  return (
    page.subPages.find(
      (subPage) => subPage.search && new URLSearchParams(subPage.search).get('tab') === tab
    ) ?? page.subPages[0]
  );
};

export default NAV_TREE;
